import { useCallback, useEffect } from 'react';
import { Y200 } from '@atlaskit/theme/colors';
import { fg } from '@atlassian/jira-feature-gating';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { SupportedThirdParty } from '../../../../common/constants/import-type';
import type { RequestAccessTokenMethod, ThirdPartyEntity } from '../../../../common/types';
import { useContentForThirdParty } from '../../../../common/utils';
import { useThirdPartyState } from '../../../../controllers/import-state';
import { useThirdPartyFolders } from '../../../../services/third-party-folders';

type Props = {
	thirdParty: SupportedThirdParty;
	shouldShowPlaceholderIcons: boolean;
	requestAuthenticationToken: RequestAccessTokenMethod;
	shouldShowFolderSelector: boolean;
	parentEntityId: string | null;
};

export const useFolders = ({
	thirdParty,
	shouldShowPlaceholderIcons,
	requestAuthenticationToken,
	shouldShowFolderSelector,
	parentEntityId,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		foldersSelectorLabel: label,
		foldersSelectorPlaceholder: placeholder,
		errorOccurredFetchingFoldersHeader: errorOccurredHeader,
	} = useContentForThirdParty(thirdParty);

	const { fetchThirdPartyFolders, hasErrorOccurred, isLoading } = useThirdPartyFolders({
		thirdParty,
		requestAuthenticationToken,
	});

	const [{ folders, selectedWorkspaceId, selectedFolderId }, { setThirdPartyState }] =
		useThirdPartyState();

	const onFetchFolders = useCallback(() => {
		if (
			selectedWorkspaceId === null ||
			folders != null ||
			(parentEntityId === null && fg('jwm_clickup_importer'))
		) {
			return;
		}

		const entityId =
			parentEntityId !== null && fg('jwm_clickup_importer') ? parentEntityId : selectedWorkspaceId;

		fetchThirdPartyFolders(entityId).then((data) => {
			setThirdPartyState({ folders: data?.folders, subfolders: data?.subfolders });
			fireUIAnalytics(createAnalyticsEvent({}), 'thirdPartyFolders retrieved', {
				thirdParty,
				numFolders: data?.folders?.length || 0,
			});
		});
	}, [
		createAnalyticsEvent,
		fetchThirdPartyFolders,
		folders,
		parentEntityId,
		selectedWorkspaceId,
		setThirdPartyState,
		thirdParty,
	]);

	useEffect(() => {
		shouldShowFolderSelector && onFetchFolders();
	}, [onFetchFolders, shouldShowFolderSelector]);

	const onChange = useCallback(
		(selectedFolder: ThirdPartyEntity | null) => {
			setThirdPartyState({
				selectedFolderId: selectedFolder ? selectedFolder.id : null,
				selectedSubfolderId: null,
				selectedEntities: null,
				selectedEntityId: null,
				entities: null,
				entityStats: null,
				members: null,
				thirdPartyFieldConfigState: null,
				thirdPartyStatusMapping: null,
			});
		},
		[setThirdPartyState],
	);

	const onClear = useCallback(() => onChange(null), [onChange]);

	return {
		label,
		placeholder,
		starredOptionsLabel: '',
		otherOptionsLabel: '',
		noEntitiesFoundHeader: '',
		noEntitiesFoundDescription: '',
		errorOccurredHeader,
		entities: folders,
		selectedEntityId: selectedFolderId,
		isLoading,
		isDisabled: selectedWorkspaceId === null,
		isClearable: true,
		hasErrorOccurred,
		onChange,
		onClear,
		onRetry: onFetchFolders,
		placeholderColor: shouldShowPlaceholderIcons ? Y200 : undefined,
		isEmptyEntities: !folders || (!isLoading && folders.length === 0),
	};
};
